import {
  AfterViewInit,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { ImportErrorService } from '../+shared/+state/import-errors/services/import-error.service';
import {
  filter,
  map,
  mergeMap,
  startWith,
  tap,
  finalize,
} from 'rxjs/operators';

import { MatTableDataSource, MatTable, MatNoDataRow, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { ClientService } from '../../+shared/services/client/client.service';
import { PageEvent } from '@angular/material/paginator';
import { DatePipe, NgClass } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { ClientLocationSelectComponent } from '../shared/components/client-location-select/client-location-select.component';
import { ImportError } from '../+shared';
import { AdminComponent } from '../admin.component';
import { MatSort } from '@angular/material/sort';
import { MatFormField, MatSuffix } from "@angular/material/form-field";
import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle } from "@angular/material/datepicker";
import { MatInput } from "@angular/material/input";
import { FormControl, ReactiveFormsModule } from "@angular/forms";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import {
  ActivatedRoute,
  Router,
} from '@angular/router';
import {MenuImportDto} from "../+shared/models/menu.import.dto";
import { MatSelect, MatOption } from '@angular/material/select';
import { ERROR_CODES, ErrorCodeDisplay } from '../+shared/+state/import-errors/models/error-code-display';

@Component({
  selector: 'dish-admin-import-errors',
  templateUrl: './admin-import-errors.component.html',
  styleUrls: ['./admin-import-errors.component.scss'],
  imports: [
    ClientLocationSelectComponent,
    MatButton,
    MatIcon,
    MatTable,
    MatNoDataRow,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatPaginator,
    DatePipe,
    AdminComponent,
    MatSort,
    MatFormField,
    MatDatepickerInput,
    MatDatepicker,
    MatInput,
    MatDatepickerToggle,
    MatSuffix,
    ReactiveFormsModule,
    NgClass,
    MatSelect,
    MatOption,
  ],
})
export class AdminImportErrorsComponent implements OnInit, AfterViewInit {
  private readonly importErrorService = inject(ImportErrorService);
  private readonly clientService = inject(ClientService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  paginator = viewChild<MatPaginator>('paginator');
  importErrors = this.importErrorService.filteredImportErrors;
  dataSource = new MatTableDataSource<ImportError>();
  displayedColumns: string[] = [
    'clientName',
    'locationName',
    'code',
    'objectId',
    'message',
    'resolution',
    'createdOn',
    'actions'
  ];
  selectedDate = signal<Date | null>(null);
  mealId = toSignal<number | undefined>(
    this.clientService.currentMeal$.pipe(map((meal) => meal?.id)),
    { initialValue: undefined }
  );
  locationId = toSignal<number | undefined>(
    this.clientService.currentSelectedLocation$.pipe(
      map((location) => location?.id)
    )
  );
  clientId = toSignal<number | undefined>(
    this.clientService.currentClient$.pipe(map((client) => client?.id))
  );
  dateControl = new FormControl({ value: '', disabled: true });
  codeControl = new FormControl<string | undefined>(undefined);
  destroyRef = inject(DestroyRef);

  // Track import-in-progress state keyed by objectId string
  loadingImports = signal<Record<string, boolean>>({});
  // Error code filtering
  errorCodes: ErrorCodeDisplay[] = ERROR_CODES;
  selectedCode = signal<string | undefined>(undefined);

  // Pagination
  pageSize = signal<number>(5);
  pageIndex = signal<number>(0);
  totalCount = signal<number>(0);
  hasMoreItems = signal<boolean>(false);

  isImporting(key: string): boolean {
    if (!key) return false;
    return !!this.loadingImports()[key];
  }

  /**
   * Checks if the specific import error is currently being imported.
   * Uses a composite key of clientId + locationId + menuId + date.
   * When any item for a specific date is being imported, all items
   * for that same date should be disabled since the backend processes
   * them together.
   */
  isImportingMenu(importError: ImportError): boolean {
    const key = this.getImportKey(importError);
    // Check if this specific item is being imported
    if (this.isImporting(key)) {
      return true;
    }
    // Check if any item with the same clientId + locationId + menuId + date is being imported
    return Object.keys(this.loadingImports()).some(k => {
      // Key format: clientId-locationId-menuId-date (YYYY-MM-DD)
      // Check if this key matches our clientId, locationId, menuId, and date
      const keyParts = k.split('-');
      const keyClientId = keyParts[0];
      const keyLocationId = keyParts[1];
      const keyMenuId = keyParts[2];
      const keyDate = keyParts.slice(3).join('-');

      const errorDate = new Date(importError.date).toISOString().split('T')[0];

      return keyClientId === importError.clientId.toString() &&
             keyLocationId === importError.locationId.toString() &&
             keyMenuId === importError.objectId &&
             keyDate === errorDate &&
             this.loadingImports()[k];
    });
  }

  private setImporting(key: string, value: boolean) {
    const current = { ...this.loadingImports() };
    if (value) {
      current[key] = true;
    } else {
      delete current[key];
    }
    this.loadingImports.set(current);
  }

  ngOnInit(): void {
    // Initialize from query string if clientId is present
    const qpClientId = this.route.snapshot.queryParamMap.get('clientId');
    if (qpClientId) {
      const idNum = Number(qpClientId);
      if (!isNaN(idNum)) {
        this.clientService
          .getById(idNum)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe();
      }
    }

    this.fetchImportErrorsOnChange();

    // Enable/disable date picker and sync query param on client change
    this.clientService.currentClient$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((client) => {
        // Toggle date control
        if (client) {
          this.dateControl.enable();
        } else {
          this.dateControl.disable();
        }
        // Sync query param
        const clientId = client?.id ?? null;
        void this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { clientId },
          queryParamsHandling: 'merge',
          replaceUrl: true,
        });
      });
  }

  ngAfterViewInit() {
    // Do not connect dataSource.paginator for server-side pagination
    // The paginator is controlled manually via the (page) event
  }

  fetchImportErrorsOnChange(resetDate = true) {
    this.clientService.currentClient$
      .pipe(
        startWith(),
        filter((client) => {
          // Allow fetching when client is selected OR when error code is selected
          // Some error codes (e.g., ClientIntegrationForbidden) aren't tied to clients
          return client != null || this.selectedCode() != null;
        }),
        mergeMap((client) =>
          this.importErrorService
            .getBy(
              client?.id,
              this.locationId(),
              this.mealId(),
              this.selectedDate(),
              this.selectedCode(),
              this.pageSize() + 1 // Fetch one extra to check if there's a next page
            )
            .pipe(
              tap((importErrors) => {
                // Check if we got more items than the page size (indicates there's a next page)
                const hasMore = importErrors.length > this.pageSize();
                this.hasMoreItems.set(hasMore);

                // Calculate total count for the paginator
                const currentPage = this.importErrorService.importErrorCurrentPage();
                const itemsOnPage = Math.min(importErrors.length, this.pageSize());

                // Calculate minimum total based on current page
                const currentMinTotal = ((currentPage - 1) * this.pageSize()) + itemsOnPage;

                let calculatedTotal: number;
                if (hasMore) {
                  // If there's a next page, we need to show at least 2 pages worth
                  // This ensures both Next and Last buttons are enabled
                  calculatedTotal = currentMinTotal + this.pageSize();
                } else {
                  // If no next page, this is the exact total
                  calculatedTotal = currentMinTotal;
                }

                // Only update totalCount if it's greater than current (never decrease)
                // This prevents the total from shrinking when navigating backwards
                if (calculatedTotal > this.totalCount()) {
                  this.totalCount.set(calculatedTotal);
                }

                // Use the API response directly to avoid showing stale cached rows
                // Only show pageSize items (remove the extra one we fetched)
                this.setDataSources(importErrors.slice(0, this.pageSize()));
              })
            )
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
    if (resetDate) {
      this.selectedDate.set(undefined);
    }
  }

  setDataSources(importErrors: ImportError[]) {
    this.dataSource.data = importErrors;
  }

  onCodeChange(code?: string) {
    // Normalize value and update selection
    const normalized = code ?? undefined;
    this.selectedCode.set(normalized);
    // Reset paging and cached results so we only show the newly filtered data
    this.importErrorService.resetCurrentImportErrorPage();
    this.importErrorService.resetImportErrors();
    // Reset pagination state when filter changes
    this.pageIndex.set(0);
    this.totalCount.set(0);
    this.hasMoreItems.set(false);
    // Refetch with the new filter
    this.fetchImportErrorsOnChange();
  }

  onPageChange(event: PageEvent) {
    // Handle page size change
    if (event.pageSize !== this.pageSize()) {
      this.pageSize.set(event.pageSize);
      this.pageIndex.set(0);
      // Reset to first page and clear total count when page size changes
      this.importErrorService.resetCurrentImportErrorPage();
      this.totalCount.set(0);
      this.hasMoreItems.set(false);
      this.fetchImportErrorsOnChange(false);
      return;
    }

    // Handle page index change
    this.pageIndex.set(event.pageIndex);
    this.importErrorService.setCurrentImportErrorPage(event.pageIndex + 1);
    this.fetchImportErrorsOnChange(false);
  }

  dateChange(event?: Date) {
    this.selectedDate.set(event);
    // Reset pagination when date filter changes
    this.importErrorService.resetCurrentImportErrorPage();
    this.pageIndex.set(0);
    this.totalCount.set(0);
    this.hasMoreItems.set(false);
    this.fetchImportErrorsOnChange();
  }

  resetDate() {
    this.dateControl.reset();
  }

  resetImportErrors() {
    this.importErrorService.resetCurrentImportErrorPage();
    this.importErrorService.resetImportErrors();
    this.selectedDate.set(undefined);
    this.pageIndex.set(0);
    this.totalCount.set(0);
    this.hasMoreItems.set(false);
  }

  importMissingClientProducts(ids: string, clientId: number) {
    const key = ids;
    const recipeIds = ids.split(',').map((id) => parseInt(id));
    this.setImporting(key, true);
    this.importErrorService
      .importMissingRecipes(clientId, recipeIds)
      .pipe(
        tap((_) => {
          this.dataSource.data = this.importErrors();
        }),
        finalize(() => this.setImporting(key, false))
      )
      .subscribe();
  }

  /**
   * Generates a unique key for tracking import state based on
   * the combination of clientId, locationId, menuId, and date.
   * This ensures that items with the same menuId but different dates
   * or locations are treated as separate import operations.
   * Format: clientId-locationId-menuId-date (YYYY-MM-DD)
   */
  private getImportKey(importError: ImportError): string {
    const date = new Date(importError.date);
    const formattedDate = date.toISOString().split('T')[0];
    return `${importError.clientId}-${importError.locationId}-${importError.objectId}-${formattedDate}`;
  }

  importMissingMenu(importError: ImportError) {
      const key = this.getImportKey(importError);
      this.setImporting(key, true);

      const date = new Date(importError.date);
      const formattedDate = date.toISOString().split('T')[0];

      const menuDto: MenuImportDto = {
          menuId: parseInt(importError.objectId),
          date: formattedDate,
          clientId: importError.clientId,
          locationId: importError.locationId,
          mealId: importError.mealId
      };
      this.importErrorService.importMissingMenu(menuDto).pipe(
        tap((_) => {
          this.dataSource.data = this.importErrors();
        }),
        finalize(() => this.setImporting(key, false))
      ).subscribe();
  }
}
